/**
 * Lightweight command/state bus for the global Jukebox.
 *
 * - Any component (e.g. the Hero "Listen Now" button) can dispatch commands
 *   like `toggle` or `playFirst` without owning a reference to the player.
 * - The Jukebox component reports its live state (playing / hasItems / title)
 *   so remote triggers can mirror the correct icon + label.
 */

export type JukeboxCmd =
  | { type: 'playFirst' }
  | { type: 'toggle' }
  | { type: 'pause' }
  | {
      type: 'enqueueTrack';
      track: { id: string; title: string; artist: string; audioUrl: string; coverUrl?: string | null; externalUrl?: string | null };
    };

export interface JukeboxState {
  ready: boolean;
  enabled: boolean;
  hasItems: boolean;
  playing: boolean;
  title: string | null;
}

type CmdListener = (c: JukeboxCmd) => void;
type StateListener = (s: JukeboxState) => void;

let state: JukeboxState = { ready: false, enabled: false, hasItems: false, playing: false, title: null };
const cmdListeners = new Set<CmdListener>();
const stateListeners = new Set<StateListener>();

export const jukeboxBus = {
  // --- commands (sender) ---
  fire(cmd: JukeboxCmd) {
    cmdListeners.forEach((l) => l(cmd));
  },
  onCmd(l: CmdListener) {
    cmdListeners.add(l);
    return () => { cmdListeners.delete(l); };
  },

  // --- state (receiver) ---
  getState: () => state,
  setState(patch: Partial<JukeboxState>) {
    state = { ...state, ...patch };
    stateListeners.forEach((l) => l(state));
  },
  onState(l: StateListener) {
    stateListeners.add(l);
    l(state);
    return () => { stateListeners.delete(l); };
  },
};
